import { readonly } from 'vue';
import {
  useBrowserLocation, injectLocal, provideLocal, toValue,
} from '@vueuse/core';
import { buildUrl } from '@utils/commonUtils';
import { REDIRECT_MAP } from '../constants/common';

/**
 * @type {import('vue').InjectionKey<import('vue').Ref<Object<string,*>>>}
 */
const URL_LOCATION_KEY_REPOSITORY = Symbol.for('URL_LOCATION_KEY_REPOSITORY');

/**
 * constructs base url, based on the path mapping
 *
 * @private
 * @param {string} path
 * @returns {string}
 */
const getBaseUrl = (path) => path.split('.').reduce((acc, prop) => acc && acc?.[prop], REDIRECT_MAP) || '';

/**
 * returns location data in the appropriate format
 *
 * @private
 * @param {import('vue').Ref<BrowserLocation>} location
 * @returns {{
 *  locationRef: import('vue').Ref<BrowserLocation>,
 *  locationReadOnly: BrowserLocationReadOnly,
 * }}
 */
const getLocationEntry = (location) => ({
  // in case any values need to be watched or computed
  locationRef: location,
  // include the unref values in the entry
  locationReadOnly: readonly(toValue(location)),
});

/**
 * stores location data based on the default 'window' object
 */
export function useProvideUrlLocationOperations($window = null) {
  const locationOptions = { ...(!!$window && { window: $window }) };

  /** @type {import('vue').Ref<BrowserLocation>} */
  const location = useBrowserLocation(locationOptions);
  provideLocal(URL_LOCATION_KEY_REPOSITORY, getLocationEntry(location));
}

/**
 * offers a wide range of operations and selections related to the url location
 *
 * @returns {{
 * location: BrowserLocationReadOnly,
 * getSearchParamsData: getSearchParamsDataFunc,
 * getUrlData:  getUrlDataFunc,
 * redirectUrl: redirectUrlFunc,
 * }}
 */
export function useUrlLocationOperations() {
  const { locationRef, locationReadOnly } = injectLocal(URL_LOCATION_KEY_REPOSITORY, () => ({}));

  if (!locationRef || !locationReadOnly) {
    throw new Error('useUrlLocationOperations must be used within a useProvideUrlLocationOperations');
  }

  /** **** GETTERS ***** */

  /**
   * @example: ?foo=bar -> {object}
   * @callback getSearchParamsDataFunc
   * @return {module:url.URLSearchParams}
   */
  const getSearchParamsData = () => new URLSearchParams(locationRef.value.search);

  /**
   * @example http://example.com/index.php?foo=bar -> {object}
   * @callback getUrlDataFunc
   * @return {module:url.URL}
   */
  const getUrlData = () => new URL(locationRef.value.href);

  /** **** VOID ACTIONS ***** */

  /**
   * redirects to specified url based on type selection and in addition to query parameters
   * @callback redirectUrlFunc
   * @param {string} type
   * @param {object} [queryParams={}]
   */
  const redirectUrl = (type, queryParams = {}) => {
    const baseUrl = getBaseUrl(type);
    if (baseUrl) {
      locationRef.value.href = buildUrl(baseUrl, queryParams);
    }
  };

  return {
    location: locationReadOnly,
    getSearchParamsData,
    getUrlData,
    redirectUrl,
  };
}
